import * as React from "react";
import { useState } from "react";
import { Autocomplete } from "@mui/material";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { Box } from "@mui/system";
import { ErrorRounded, KeyboardRounded } from "@mui/icons-material";
import { AsyncAutocompleteProps } from "../constants/FinanceModels";

enum STATUS {
    WAITING, LOADING, DONE
}

const AsyncAutocomplete = (props: AsyncAutocompleteProps) => {

    const { t } = useTranslation('finance');

    const [delay, setDelay] = useState<NodeJS.Timeout | undefined>(undefined);
    const [status, setStatus] = useState(STATUS.WAITING);
    const [options, setOptions] = useState<readonly any[]>([]);

    function getEmptyDesign() {
        return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ErrorRounded sx={{ marginRight: '10px' }}/>
                    {t('autocomplete.no-results')}
                </Box>
        );
    }

    function getWaitingDesign() {
        return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <KeyboardRounded sx={{ marginRight: '10px' }}/>
                    {t('autocomplete.waiting')}
                </Box>
        );
    }

    function getLoadingDesign() {
        return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress color="inherit" size={'2ch'} sx={{ marginRight: '10px' }}/>
                    {t('autocomplete.loading')}
                </Box>
        );
    }

    function playCallback(value: string) {
        if (delay !== null) {
            clearTimeout(delay);
        }
        if (!props.initialValues && value.trim().length === 0) {
            setOptions([]);
            setStatus(STATUS.WAITING)
            return;
        }
        setStatus(STATUS.LOADING)
        setDelay(setTimeout(() => {
            props.callback(value).then((it) => {
                setOptions(it);
                setStatus(STATUS.DONE);
            })
        }, 850));
    }

    return (
            <Autocomplete
                    disabled={props.disabled === true}
                    multiple={props.multiselect === true}
                    clearOnEscape
                    value={props.value}
                    renderInput={props.renderInput}
                    getOptionDisabled={() => {
                        if (props.multiselect === true) {
                            return props.value.length >= 5
                        }
                        return false;
                    }}
                    loading={status !== STATUS.DONE}
                    loadingText={status === STATUS.LOADING ? getLoadingDesign() : getWaitingDesign()}
                    noOptionsText={getEmptyDesign()}
                    options={options}
                    isOptionEqualToValue={(option, value) => {
                        if (option !== null && option.hasOwnProperty("value")) {
                            return option.key === value.key;
                        }
                        return option === value;
                    }}
                    getOptionLabel={(option) => {
                        if (props.getOptionLabel != null) {
                            let label = props.getOptionLabel(option);
                            return label || option;
                        }
                        if (option !== null && option.hasOwnProperty("value")) {
                            return option.value;
                        }
                        return option;
                    }}
                    onClose={() => {
                        setStatus(STATUS.WAITING);
                    }}
                    onBlur={() => {
                        if (props.initialValues && (!props.value || props.value.length === 0)) {
                            setOptions([]);
                        }
                    }}
                    onFocus={() => {
                        if (props.initialValues && (!props.value || props.value.length === 0)) {
                            playCallback("");
                        }
                    }}
                    onChange={(event, value) => props.onChange(value)}
                    onInputChange={(event: React.SyntheticEvent, value: string) => {
                        playCallback(value);
                    }}
            />
    )
}

export default AsyncAutocomplete;